import { GROUP_PAGE, nodeLabel, type ViewNode, type VisibleGraph } from './graphModel';

/** A row of the tree: a visible node, or the "Show 30 more" row that ends an expanded group with members left. */
export type Row = {
  /** The node id; `more:<groupId>` for a "Show 30 more" row. */
  id: string;
  /** 1 for the root. */
  depth: number;
  kind: ViewNode['kind'] | 'more';
  label: string;
  /** It shows children, or would once opened (see `VisibleGraph.expandableIds`); the root when it has any. */
  expandable: boolean;
  /** Its children show: the root always, another node when it is in `expandedIds`. */
  expanded: boolean;
  /** The row it sits under; `null` for the root. */
  parentId: string | null;
  /** The row's place among the rows under the same parent, from 1, and how many there are. */
  posInSet: number;
  setSize: number;
  /** Group rows: the members shown under it and all of them. */
  group?: { shown: number; total: number };
};

const MORE_PREFIX = 'more:';
export const moreRowId = (groupId: string) => `${MORE_PREFIX}${groupId}`;

/** The name of a node's row: the card's title for a real node, the group's or the project's label otherwise. */
export function rowLabel(node: ViewNode): string {
  return node.kind === 'group' || node.kind === 'project' ? node.label : nodeLabel(node);
}

/**
 * The visible nodes as tree rows, depth first from the root (the first of `visible.nodes`): each node's children
 * are the nodes placed under it (`parentOf`), in the order the view lists them, and an expanded group with members
 * left ends with a "Show 30 more" row.
 */
export function flattenTree(visible: VisibleGraph, expandedIds: ReadonlySet<string>): Row[] {
  const root = visible.nodes[0];
  if (!root) return [];
  const children = new Map<string, ViewNode[]>();
  for (const node of visible.nodes) {
    const parent = visible.parentOf.get(node.id);
    if (parent === undefined) continue;
    const siblings = children.get(parent);
    if (siblings) siblings.push(node);
    else children.set(parent, [node]);
  }
  const rows: Row[] = [];
  const walk = (node: ViewNode, depth: number, parentId: string | null, posInSet: number, setSize: number) => {
    const isRoot = node.id === root.id;
    const under = children.get(node.id) ?? [];
    const more = node.kind === 'group' && node.remaining > 0 && expandedIds.has(node.id);
    rows.push({
      id: node.id,
      depth,
      kind: node.kind,
      label: rowLabel(node),
      expandable: isRoot ? under.length > 0 : visible.expandableIds.has(node.id),
      expanded: isRoot || expandedIds.has(node.id),
      parentId,
      posInSet,
      setSize,
      ...(node.kind === 'group' ? { group: { shown: node.shown, total: node.total } } : {}),
    });
    const size = under.length + (more ? 1 : 0);
    under.forEach((child, i) => walk(child, depth + 1, node.id, i + 1, size));
    if (more) {
      rows.push({
        id: moreRowId(node.id),
        depth: depth + 1,
        kind: 'more',
        label: `Show ${GROUP_PAGE} more`,
        expandable: false,
        expanded: false,
        parentId: node.id,
        posInSet: size,
        setSize: size,
      });
    }
  };
  walk(root, 1, null, 1, 1);
  return rows;
}

/** What a key on the focused row asks for. */
export type TreeMove =
  | { kind: 'focus'; index: number }
  | { kind: 'expand'; ids: string[] }
  | { kind: 'collapse'; id: string };

/** The first row under the row at `index`, when the next row is one. */
const firstChild = (rows: readonly Row[], index: number) =>
  rows[index + 1]?.parentId === rows[index].id ? index + 1 : null;

/**
 * The tree's keys on the row at `index`, as the ARIA tree pattern has them: Up and Down move one row, Home and End
 * to the first and last; Right opens a closed row, or moves to the first row under an open one; Left closes an
 * open row (not the root, which always shows its children), or moves to the row it sits under; `*` opens every
 * closed row beside it. `null` for any other key, or a key with nothing to do.
 */
export function nextFocus(rows: readonly Row[], index: number, key: string): TreeMove | null {
  const row = rows[index];
  if (!row) return null;
  const focus = (to: number | null) => (to === null || to < 0 || to >= rows.length ? null : { kind: 'focus' as const, index: to });
  switch (key) {
    case 'ArrowDown':
      return focus(index + 1);
    case 'ArrowUp':
      return focus(index - 1);
    case 'Home':
      return focus(0);
    case 'End':
      return focus(rows.length - 1);
    case 'ArrowRight':
      if (row.expandable && !row.expanded) return { kind: 'expand', ids: [row.id] };
      return row.expanded ? focus(firstChild(rows, index)) : null;
    case 'ArrowLeft':
      if (row.expanded && row.parentId !== null && firstChild(rows, index) !== null)
        return { kind: 'collapse', id: row.id };
      return row.parentId === null ? null : focus(rows.findIndex((r) => r.id === row.parentId));
    case '*': {
      const ids = rows.flatMap((r) => (r.parentId === row.parentId && r.expandable && !r.expanded ? [r.id] : []));
      return ids.length > 0 ? { kind: 'expand', ids } : null;
    }
    default:
      return null;
  }
}
